import { useEffect, useMemo, useRef, useState } from 'react';
import { ParliamentarianHeader } from '@/components/ParliamentarianHeader';
import { NavigationHeader } from '@/components/NavigationHeader';
import { AmendmentSection } from '@/components/AmendmentSection';
import { TotalsSummary } from '@/components/TotalsSummary';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import { getCategoryColors } from '@/lib/category-colors';
import {
  listCouncilors,
  listInstitutions,
  listAmendments,
  listAmendmentsByCouncilor,
  type Councilor,
  type Institution,
  type Amendment,
} from '@/services/firestore';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import type { TooltipItem } from 'chart.js';
import { jsPDF } from 'jspdf';
import { buildCouncilorMonthlyDataset, buildInstitutionTotalsDataset } from '@/lib/charts-data';
import '@/styles/charts.css';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatIsoDateToPtBr } from '@/lib/utils';
import { InstitutionAmendmentModal } from '@/components/InstitutionAmendmentModal';

type StyleCategory = 'health' | 'education' | 'infrastructure' | 'diverse';
type ReportItem = { id: string; title?: string; description: string; value: number };
type SortMode = 'valueAsc' | 'valueDesc' | 'nameAsc' | 'nameDesc';

export default function AmendmentReport() {
  const { toast } = useToast();
  const [councilors, setCouncilors] = useState<Councilor[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [selectedCouncilor, setSelectedCouncilor] = useState<Councilor | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAmendments, setLoadingAmendments] = useState(false);
  const [loadingTotals, setLoadingTotals] = useState(false);
  const [councilorAmendments, setCouncilorAmendments] = useState<Amendment[]>([]);

  // Painel de gráficos
  const [chartModel, setChartModel] = useState<'bar' | 'line' | 'pie' | 'doughnut' | 'polarArea' | 'radar' | 'bar_horizontal'>('bar'); // modelo do gráfico
  const [chartFilterMode, setChartFilterMode] = useState<'period' | 'councilor' | 'institution'>('period');
  const [chartCouncilorId, setChartCouncilorId] = useState<string>('');
  const [chartYear, setChartYear] = useState<string>('');
  const [chartInstitutionId, setChartInstitutionId] = useState<string>('');
  const [chartSortMode, setChartSortMode] = useState<SortMode>('valueDesc');
  const [chartSearchQuery, setChartSearchQuery] = useState<string>('');
  // Container para manipular tela cheia
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Filtros e ordenação para seção de totais por instituição
  const [filterMonth, setFilterMonth] = useState<string>(''); // "01".."12" ou '' para todos
  const [filterYear, setFilterYear] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const institutionMap = useMemo(() => {
    const map: Record<string, Institution> = {};
    institutions.forEach((i) => (map[i.id] = i));
    return map;
  }, [institutions]);

  const [sections, setSections] = useState<Record<string, ReportItem[]>>({});
  const [detailAmendmentId, setDetailAmendmentId] = useState<string | null>(null);

  // Helpers para mapear categorias do sistema em estilos conhecidos
  const normalize = (s: string) => s
    ?.normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
  const styleForCategory = (cat: string): StyleCategory => {
    const n = normalize(cat || '');
    if (n.includes('saude')) return 'health';
    if (n.includes('educa')) return 'education';
    if (n.includes('infra')) return 'infrastructure';
    return 'diverse';
  };
  const iconForStyle: Record<StyleCategory, string> = {
    health: 'fas fa-heartbeat',
    education: 'fas fa-graduation-cap',
    infrastructure: 'fas fa-road',
    diverse: 'fas fa-th-large',
  };

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [cs, insts, amds] = await Promise.all([listCouncilors(), listInstitutions(), listAmendments()]);
        setCouncilors(cs);
        setInstitutions(insts);
        setAmendments(amds);
        // Pré-seleciona ano atual se houver dados
        const years = Array.from(new Set(amds.map((a) => (a.date || '').slice(0, 4)).filter(Boolean)));
        if (years.includes(String(new Date().getFullYear()))) setFilterYear(String(new Date().getFullYear()));
        try {
          const m = localStorage.getItem('ar.chart.model');
          const fm = localStorage.getItem('ar.chart.filterMode');
          const cy = localStorage.getItem('ar.chart.year');
          const cc = localStorage.getItem('ar.chart.councilorId');
          const ci = localStorage.getItem('ar.chart.institutionId');
          const sm = localStorage.getItem('ar.chart.sortMode');
          const sq = localStorage.getItem('ar.chart.search');
          if (m) setChartModel(m as any);
          if (fm) setChartFilterMode(fm as any);
          if (cy) setChartYear(cy);
          if (cc) setChartCouncilorId(cc);
          if (ci) setChartInstitutionId(ci);
          if (sm) setChartSortMode(sm as SortMode);
          if (sq) setChartSearchQuery(sq);
        } catch { void 0; }
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('ar.chart.model', String(chartModel));
      localStorage.setItem('ar.chart.filterMode', String(chartFilterMode));
      localStorage.setItem('ar.chart.year', String(chartYear));
      localStorage.setItem('ar.chart.councilorId', String(chartCouncilorId));
      localStorage.setItem('ar.chart.institutionId', String(chartInstitutionId));
      localStorage.setItem('ar.chart.sortMode', String(chartSortMode));
      localStorage.setItem('ar.chart.search', String(chartSearchQuery));
    } catch { void 0; }
  }, [chartModel, chartFilterMode, chartYear, chartCouncilorId, chartInstitutionId, chartSortMode, chartSearchQuery]);

  const handleSelectCouncilor = async (c: Councilor) => {
    setSelectedCouncilor(c);
    setLoadingAmendments(true);
    try {
      const amendments = await listAmendmentsByCouncilor(c.id);
      setCouncilorAmendments(amendments);
      const next: Record<string, ReportItem[]> = {};
      amendments.forEach((a) => {
        const inst = institutionMap[a.institutionId];
        const sysCat = (inst?.category || '').trim() || 'Áreas Diversas';
        if (!next[sysCat]) next[sysCat] = [];
        // Nome completo da instituição como título; descrição da emenda como corpo
        next[sysCat].push({ id: a.id, title: inst?.name, description: a.description, value: a.value });
      });
      setSections(next);
    } finally {
      setLoadingAmendments(false);
    }
  };

  // Estado para o modal de detalhes da instituição
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedInstitutionGroup, setSelectedInstitutionGroup] = useState<{ name: string; institutionIds: string[] } | null>(null);

  const [returnToInstitutionList, setReturnToInstitutionList] = useState(false);

  // ... (existing code)

  // In the Institution List Modal (Dialog)


  // ======= Seção: Totais por Instituição (Consolidado por CNPJ) =======
  const yearsAvailable = useMemo(() => {
    const ys = Array.from(new Set(amendments.map((a) => (a.date || '').slice(0, 4)).filter(Boolean)));
    return ys.sort();
  }, [amendments]);

  const computeInstitutionTotals = useMemo(() => {
    // Aplica filtros de período nos dados brutos
    const filtered = amendments.filter((a) => {
      const d = a.date || '';
      const y = d.slice(0, 4);
      const m = d.slice(5, 7);
      if (filterYear && y !== filterYear) return false;
      if (filterMonth && m !== filterMonth) return false;
      return true;
    });

    // Agrupa por CNPJ (ou ID se não tiver CNPJ)
    const groups = new Map<string, {
      uniqueKey: string;
      name: string;
      cnpj?: string;
      total: number;
      institutionIds: Set<string>;
      categories: Set<string>;
    }>();

    for (const a of filtered) {
      const inst = institutionMap[a.institutionId];
      if (!inst) continue;

      // Chave de agrupamento: CNPJ (apenas números) ou ID
      const cleanCnpj = inst.cnpj ? inst.cnpj.replace(/\D/g, '') : '';
      const key = cleanCnpj || inst.id;

      if (!groups.has(key)) {
        groups.set(key, {
          uniqueKey: key,
          name: inst.name, // Usa o nome da primeira ocorrência
          cnpj: inst.cnpj,
          total: 0,
          institutionIds: new Set(),
          categories: new Set()
        });
      }
      const page = groups.get(key)!;
      page.total += (a.value || 0);
      page.institutionIds.add(inst.id);
      if (inst.category) page.categories.add(inst.category);
    }

    const rows = Array.from(groups.values()).map(g => ({
      id: g.uniqueKey, // ID "virtual" do grupo
      name: g.name,
      total: g.total,
      institutionIds: Array.from(g.institutionIds),
      categoriesDisplay: Array.from(g.categories).join(', ')
    }));

    // Aplica busca por nome ou categoria
    const q = chartSearchQuery.toLowerCase();
    const searched = chartSearchQuery
      ? rows.filter((r) => {
        const cat = r.categoriesDisplay.toLowerCase();
        return r.name.toLowerCase().includes(q) || cat.includes(q);
      })
      : rows;

    // Ordena por valor
    const sorted = searched.sort((a, b) => sortOrder === 'asc' ? a.total - b.total : b.total - a.total);
    return sorted;
  }, [amendments, institutionMap, filterYear, filterMonth, chartSearchQuery, sortOrder]);

  // Aliases para compatibilidade com o código existente do filterYear/Month (que usam nomes de estado locais iguais mas vale garantir)
  const filteredYear = filterYear;
  const filteredMonth = filterMonth;

  const maxTotal = useMemo(() => computeInstitutionTotals.reduce((m, r) => Math.max(m, r.total), 0), [computeInstitutionTotals]);

  const openInstitutionDetails = (name: string, institutionIds: string[]) => {
    setSelectedInstitutionGroup({ name, institutionIds });
    setDetailsModalOpen(true);
  };

  const exportCsv = () => {
    try {
      const header = ['Instituição', 'Categorias', 'Total (num)', 'Total (BRL)'];
      const rows = computeInstitutionTotals.map((r) => [
        r.name,
        r.categoriesDisplay,
        String(r.total),
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(r.total),
      ]);
      const csv = [header.join(','), ...rows.map((row) => row.map((cell) => {
        // Escapa vírgulas e aspas
        const s = String(cell);
        if (s.includes(',') || s.includes('"')) return '"' + s.replace(/"/g, '""') + '"';
        return s;
      }).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthLabel = filterMonth ? `-${filterMonth}` : '';
      const yearLabel = filterYear ? `-${filterYear}` : '';
      a.download = `totais-instituicoes-consolidado${yearLabel}${monthLabel}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Falha ao exportar CSV', description: 'Tente novamente mais tarde.', variant: 'destructive' });
    }
  };

  // ... (Chart logic remains mostly same, ensuring yearsAvailable is defined above) ...

  // ======= Painel de Gráficos (Chart.js) =======

  const chartSourceAmendments = useMemo(() => {
    let src = amendments;
    if (chartFilterMode === 'councilor' && chartCouncilorId) {
      src = src.filter((a) => a.councilorId === chartCouncilorId);
    }
    return src;
  }, [amendments, chartFilterMode, chartCouncilorId]);

  const chartInstitutionCouncilorRows = useMemo(() => {
    if (chartFilterMode !== 'institution' || !chartInstitutionId) return [] as { id: string; name: string; total: number }[];
    const filtered = amendments.filter((a) => {
      if (a.institutionId !== chartInstitutionId) return false;
      const y = (a.date || '').slice(0, 4);
      if (chartYear && y !== chartYear) return false;
      return true;
    });
    const totals = new Map<string, number>();
    for (const a of filtered) {
      const prev = totals.get(a.councilorId) || 0;
      totals.set(a.councilorId, prev + (a.value || 0));
    }
    const rows: { id: string; name: string; total: number }[] = Array.from(totals.entries()).map(([cId, total]) => ({ id: cId, name: councilors.find((c) => c.id === cId)?.name || 'Vereador', total }));
    const searched = chartSearchQuery ? rows.filter((r) => r.name.toLowerCase().includes(chartSearchQuery.toLowerCase())) : rows;
    const byNameAsc = (a: { name: string; total: number }, b: { name: string; total: number }) => a.name.localeCompare(b.name, 'pt-BR');
    const byValueAsc = (a: { name: string; total: number }, b: { name: string; total: number }) => a.total - b.total;
    const sorted = [...searched].sort((a, b) => {
      if (chartSortMode === 'nameAsc') return byNameAsc(a, b);
      if (chartSortMode === 'nameDesc') return byNameAsc(b, a);
      if (chartSortMode === 'valueAsc') return byValueAsc(a, b);
      return byValueAsc(b, a);
    });
    return sorted;
  }, [amendments, chartInstitutionId, chartYear, councilors, chartFilterMode, chartSortMode, chartSearchQuery]);

  const chartDataset = useMemo(() => {
    if (chartFilterMode === 'institution' && chartInstitutionId) {
      const labels = chartInstitutionCouncilorRows.map((r) => r.name);
      const data = chartInstitutionCouncilorRows.map((r) => r.total);
      return { labels, data };
    }
    // Note: buildInstitutionTotalsDataset might need adjustment if visual consistency with consolidated table is desired,
    // but for now keeping it as per institutionId to avoid breaking chart logic which might rely on individual IDs.
    // The user strictly asked for consolidation in the "Valor Total table".
    return buildInstitutionTotalsDataset(
      chartSourceAmendments,
      institutions,
      chartYear || undefined,
      undefined,
      chartSearchQuery || undefined,
    );
  }, [chartFilterMode, chartYear, chartInstitutionId, chartInstitutionCouncilorRows, chartSourceAmendments, institutions, chartSearchQuery]);

  const renderChart = () => {
    const canvas = chartCanvasRef.current;
    if (!canvas) return;
    try { Chart.register(ChartDataLabels as any); } catch { }
    // Destroy previous chart to avoid leaks
    chartInstanceRef.current?.destroy();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const isEmpty = chartDataset.labels.length === 0;
    if (isEmpty) {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      chartInstanceRef.current = null;
      return;
    }
    const resolvedType = chartModel === 'bar_horizontal' ? 'bar' : chartModel;
    // Paleta vívida com variações fornecidas pelo usuário
    const LILAC = ['#D082DE', '#E0A8E9', '#EBCDEF', '#F5E6F7', '#B76BC4', '#9E53AA', '#853C90'];
    const PINK = ['#F48EB1', '#F8B0C9', '#FBD2DE', '#FEEAF0', '#DC799A', '#C46484', '#AB4F6E'];
    const MINT = ['#96DED7', '#ACE7E1', '#C1EEEB', '#D7F6F4', '#7FC6C0', '#68AEA9', '#519692'];
    const BLUE = ['#8CB8FF', '#ACD0FF', '#CBE2FF', '#E5F0FF', '#71A1E6', '#568ACC', '#3B73B3'];
    const MIXED = ['#E288C8', '#91CBEC'];
    const FULL_PALETTE = [...LILAC, ...PINK, ...MINT, ...BLUE, ...MIXED];
    const hexToRgba = (hex: string, alpha = 0.70) => {
      const h = hex.replace('#', '');
      const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
      const bigint = parseInt(full, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    const bgColors = chartDataset.labels.map((_, idx) => hexToRgba(FULL_PALETTE[idx % FULL_PALETTE.length], 0.70));
    const hoverBgColors = chartDataset.labels.map((_, idx) => hexToRgba(FULL_PALETTE[idx % FULL_PALETTE.length], 0.85));
    const borderColors = chartDataset.labels.map((_, idx) => FULL_PALETTE[idx % FULL_PALETTE.length]);
    const cfg = {
      type: resolvedType,
      data: {
        labels: chartDataset.labels,
        datasets: [
          {
            label: chartFilterMode === 'councilor' ? 'Alocações por instituição (R$)' : 'Total por instituição (R$)',
            data: chartDataset.data,
            borderColor: borderColors,
            backgroundColor: bgColors,
            hoverBackgroundColor: hoverBgColors,
            borderWidth: 2,
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { labels: { color: '#0f172a' } },
          tooltip: {
            enabled: true,
            callbacks: {
              label: (ctx: TooltipItem<'bar' | 'line' | 'pie' | 'doughnut' | 'polarArea' | 'radar'>) => {
                const label = String(ctx.label || '');
                const parsed = (ctx as unknown as { parsed?: number }).parsed;
                const val = typeof parsed === 'number' ? parsed : Number(ctx.raw || 0);
                const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
                const totalSum = chartDataset.data.reduce((acc, v) => acc + (v || 0), 0);
                const pct = totalSum > 0 ? ((val / totalSum) * 100) : 0;
                const pctStr = `${pct.toFixed(2)}%`;
                if (chartFilterMode === 'institution') {
                  const inst = institutions.find((i) => i.id === chartInstitutionId);
                  const instName = inst?.name || '';
                  const category = inst?.category || 'Categoria desconhecida';
                  const cId = councilors.find((c) => c.name === label)?.id || '';
                  const count = chartSourceAmendments.filter((a) => a.institutionId === chartInstitutionId && (!chartYear || (a.date || '').slice(0, 4) === chartYear) && (!cId || a.councilorId === cId)).length;
                  return ` Vereador: ${label}\n Instituição: ${instName}\n Categoria: ${category}\n Emendas: ${count}\n Valor: ${brl}\n Participação: ${pctStr}`;
                } else {
                  const inst = institutions.find((i) => i.name === label);
                  const category = inst?.category || 'Categoria desconhecida';
                  const cleanCnpj = inst?.cnpj ? inst.cnpj.replace(/\D/g, '') : '';
                  const count = chartSourceAmendments.filter((a) => {
                    const aInst = institutions.find(i => i.id === a.institutionId);
                    const aCnpj = aInst?.cnpj ? aInst.cnpj.replace(/\D/g, '') : '';
                    const sameGroup = (cleanCnpj && aCnpj === cleanCnpj) || (a.institutionId === inst?.id);
                    if (!sameGroup) return false;
                    if (chartYear && (a.date || '').slice(0, 4) !== chartYear) return false;
                    return true;
                  }).length;
                  return ` Instituição: ${label}\n Categoria: ${category}\n Emendas: ${count}\n Valor: ${brl}\n Participação: ${pctStr}`;
                }
              },
            },
          },
          datalabels: {
            display: ['bar', 'bar_horizontal', 'pie', 'doughnut', 'polarArea'].includes(chartModel),
            color: '#0f172a',
            font: { weight: 'bold' as const, size: 11 },
            formatter: (value: unknown, ctx: any) => {
              const val = Number(value || 0);
              const arr: number[] = Array.isArray(ctx?.dataset?.data) ? ctx.dataset.data : [];
              const sum = arr.reduce((acc: number, v: number) => acc + (Number(v) || 0), 0);
              if (!sum || val <= 0) return '';
              const pct = (val / sum) * 100;
              return `${pct.toFixed(1)}%`;
            },
            anchor: 'end' as const,
            align: 'top' as const,
            clamp: true,
            layout: { padding: 4 },
            listeners: {
              // Prevent rounding errors
            }
          },
        },
        scales: {
          x: {
            ticks: {
              color: '#334155',
              callback: (value: string | number) => {
                const s = String(value);
                if (chartFilterMode === 'institution') return s;
                const inst = institutions.find((i) => i.name === s);
                const category = inst?.category;
                return category ? `${s} (${category})` : s;
              },
            },
          },
          y: {
            ticks: {
              color: '#334155',
              callback: (value: string | number) => {
                const s = String(value);
                if (chartFilterMode === 'institution') return s;
                const inst = institutions.find((i) => i.name === s);
                const category = inst?.category;
                return category ? `${s} (${category})` : s;
              },
            },
          },
        },
      },
    } as const;
    // Opções customizadas para modelos específicos
    if (chartModel === 'bar_horizontal') {
      (cfg as any).options = {
        ...cfg.options,
        indexAxis: 'y',
      };
    }
    chartInstanceRef.current = new Chart(ctx, cfg);
  };

  useEffect(() => {
    renderChart();
    // Cleanup on unmount
    return () => chartInstanceRef.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDataset, chartModel, chartFilterMode]);

  // Ajusta o tamanho do gráfico ao entrar/sair de tela cheia
  useEffect(() => {
    const handler = () => {
      const el = chartContainerRef.current;
      const active = !!document.fullscreenElement && document.fullscreenElement === el;
      setIsFullscreen(active);
      chartInstanceRef.current?.resize();
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    const el = chartContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const exportChartPNG = () => {
    if (!chartInstanceRef.current) return;
    const url = chartInstanceRef.current.toBase64Image();
    const a = document.createElement('a');
    a.href = url;
    a.download = chartFilterMode === 'councilor' ? 'emendas-por-vereador.png' : 'totais-por-instituicao.png';
    a.click();
  };

  const exportChartPDF = () => {
    if (!chartInstanceRef.current) return;
    const url = chartInstanceRef.current.toBase64Image();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const imgProps = { width: 800, height: 450 };
    doc.addImage(url, 'PNG', 20, 40, imgProps.width, imgProps.height);
    doc.setFontSize(14);
    doc.text(chartFilterMode === 'councilor' ? 'Emendas por vereador' : 'Totais por instituição', 20, 30);
    doc.save(chartFilterMode === 'councilor' ? 'emendas-por-vereador.pdf' : 'totais-por-instituicao.pdf');
  };

  // Helper para agrupar emendas no modal
  const getGroupedAmendmentsForModal = () => {
    if (!selectedInstitutionGroup) return {};
    const ids = new Set(selectedInstitutionGroup.institutionIds);
    // Filtrar emendas que pertencem aos IDs do grupo
    const groupAmendments = amendments.filter(a => ids.has(a.institutionId));

    // Agrupar por categoria usando a lógica existente
    const groups: Record<string, ReportItem[]> = {};
    groupAmendments.forEach((a) => {
      const inst = institutionMap[a.institutionId];
      const sysCat = (inst?.category || '').trim() || 'Áreas Diversas';
      if (!groups[sysCat]) groups[sysCat] = [];
      // Título aqui mostra o nome do vereador pois estamos vendo dados DA instituição
      const councilorName = councilors.find(c => c.id === a.councilorId)?.name || 'Vereador Desconhecido';

      groups[sysCat].push({
        id: a.id,
        title: councilorName,
        description: a.description,
        value: a.value
      });
    });
    return groups;
  };

  if (!selectedCouncilor) {
    return (
      <div className="min-h-screen bg-background font-inter">
        {/* Header público com acesso ao Login */}
        <NavigationHeader />
        <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
          <header className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              <i className="fas fa-file-alt text-primary mr-3"></i>
              Relatórios de Emendas dos Vereadores
            </h1>
            <p className="text-muted-foreground">
              Selecione um vereador para visualizar seu relatório de emendas
            </p>
          </header>
          {loading ? (
            <p className="text-muted-foreground">Carregando vereadores...</p>
          ) : councilors.length === 0 ? (
            <p className="text-muted-foreground">Nenhum vereador cadastrado.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {councilors.map((c) => (
                <Card key={c.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader className="text-center">
                    <div className="flex justify-center mb-4">
                      <img
                        src={c.photoUrl || '/placeholder.svg'}
                        alt={c.name}
                        className="w-24 h-24 rounded-full border-4 border-primary object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                    </div>
                    <CardTitle className="text-lg">{c.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{c.party}</p>
                    <p className="text-sm font-medium text-primary">Vereador</p>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-4">
                        Limite Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.totalLimit)}
                      </p>
                      <Button
                        onClick={() => handleSelectCouncilor(c)}
                        className="w-full"
                        disabled={loadingAmendments}
                      >
                        <i className="fas fa-eye mr-2"></i>
                        Ver Relatório
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Seção de Totais por Instituição (visível mesmo sem selecionar vereador) */}
          <div className="mt-10">
            {/* Painel de gráficos */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Gráficos de Emendas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 mb-6">
                  {/* Filtros Principais de Dados */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="chart-filter-mode" className="text-sm font-semibold text-slate-700">1. Analisar por</label>
                      <Select value={chartFilterMode} onValueChange={(v) => setChartFilterMode(v as any)}>
                        <SelectTrigger id="chart-filter-mode" className="w-full bg-slate-50 border-slate-200">
                          <SelectValue placeholder="Período" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="period">Período (Total por Inst.)</SelectItem>
                          <SelectItem value="councilor">Vereador (Seus repasses)</SelectItem>
                          <SelectItem value="institution">Instituição (Origem repasses)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {chartFilterMode === 'councilor' && (
                      <div className="space-y-2">
                        <label htmlFor="chart-councilor" className="text-sm font-semibold text-slate-700">2. Selecione o Vereador</label>
                        <Select value={chartCouncilorId} onValueChange={(v) => setChartCouncilorId(v)}>
                          <SelectTrigger id="chart-councilor" className="w-full"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                            {councilors.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {chartFilterMode === 'institution' && (
                      <div className="space-y-2">
                        <label htmlFor="chart-institution" className="text-sm font-semibold text-slate-700">2. Selecione a Instituição</label>
                        <Select value={chartInstitutionId} onValueChange={(v) => setChartInstitutionId(v)}>
                          <SelectTrigger id="chart-institution" className="w-full"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                            {institutions
                              .slice()
                              .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'))
                              .map((i) => (
                                <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label htmlFor="chart-year" className="text-sm font-semibold text-slate-700">
                        {chartFilterMode === 'period' ? '2. Ano de Referência' : '3. Filtrar por Ano (Opcional)'}
                      </label>
                      <Select value={chartYear} onValueChange={(v) => setChartYear(v === 'all' ? '' : v)}>
                        <SelectTrigger id="chart-year" className="w-full"><SelectValue placeholder="Todos os anos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os anos</SelectItem>
                          {yearsAvailable.map((y) => (
                            <SelectItem key={y} value={y}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {chartFilterMode === 'institution' && (
                      <div className="space-y-2">
                        <label htmlFor="chart-sort" className="text-sm font-semibold text-slate-700">Ordenação</label>
                        <Select value={chartSortMode} onValueChange={(v) => setChartSortMode(v as SortMode)}>
                          <SelectTrigger id="chart-sort" className="w-full"><SelectValue placeholder="Ordenar" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="valueAsc">Valor (crescente)</SelectItem>
                            <SelectItem value="valueDesc">Valor (decrescente)</SelectItem>
                            <SelectItem value="nameAsc">Nome (A-Z)</SelectItem>
                            <SelectItem value="nameDesc">Nome (Z-A)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Controles de Visualização */}
                  <div className="flex flex-col md:flex-row gap-4 items-end border-t border-slate-100 pt-4">
                    <div className="w-full md:w-48 space-y-2">
                      <label htmlFor="chart-model" className="text-sm font-medium text-slate-600">Tipo de Visualização</label>
                      <Select value={chartModel} onValueChange={(v) => setChartModel(v as any)}>
                        <SelectTrigger id="chart-model" className="w-full"><SelectValue placeholder="Barra" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bar">Barras Verticais</SelectItem>
                          <SelectItem value="bar_horizontal">Barras Horizontais</SelectItem>
                          <SelectItem value="line">Linha</SelectItem>
                          <SelectItem value="pie">Pizza</SelectItem>
                          <SelectItem value="doughnut">Rosca (Doughnut)</SelectItem>
                          <SelectItem value="polarArea">Área Polar</SelectItem>
                          <SelectItem value="radar">Radar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="w-full flex-1 space-y-2">
                      <label htmlFor="chart-search" className="text-sm font-medium text-slate-600">Buscar no gráfico</label>
                      <Input
                        id="chart-search"
                        placeholder={chartFilterMode === 'institution' ? "Filtrar vereadores..." : "Filtrar instituições..."}
                        value={chartSearchQuery}
                        onChange={(e) => setChartSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>

                    <div className="flex gap-2 w-full md:w-auto mt-4 md:mt-0">
                      <Button variant="outline" size="icon" onClick={toggleFullscreen} title="Tela cheia">
                        <i className="fas fa-expand"></i>
                      </Button>
                      <Button variant="outline" onClick={exportChartPNG} title="Baixar imagem PNG">
                        <i className="fas fa-image mr-2"></i>
                        PNG
                      </Button>
                      <Button variant="outline" onClick={exportChartPDF} title="Baixar PDF">
                        <i className="fas fa-file-pdf mr-2"></i>
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>
                {(chartFilterMode === 'councilor' && !chartCouncilorId) || (chartFilterMode === 'institution' && !chartInstitutionId) ? (
                  <p className="text-muted-foreground">{chartFilterMode === 'councilor' ? 'Selecione um vereador para visualizar o gráfico individual.' : 'Selecione uma instituição para visualizar os vereadores com emendas.'}</p>
                ) : chartDataset.labels.length === 0 ? (
                  <p className="text-muted-foreground">Não há dados para os filtros selecionados.</p>
                ) : (
                  <div ref={chartContainerRef} className="chart-container relative w-full h-[320px] sm:h-[420px]" role="img" aria-label="Gráfico de emendas">
                    {isFullscreen && (
                      <div className="absolute top-2 left-2 right-2 z-10 flex flex-wrap items-center gap-2 bg-white/95 border border-slate-200 rounded-md p-2 shadow">
                        <div className="w-auto">
                          <label htmlFor="chart-model-fs" className="text-xs font-medium text-slate-700">Modelo</label>
                          <Select value={chartModel} onValueChange={(v) => setChartModel(v as any)}>
                            <SelectTrigger id="chart-model-fs" aria-label="Selecione o modelo do gráfico"><SelectValue placeholder="Bar" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bar">Bar</SelectItem>
                              <SelectItem value="bar_horizontal">Bar (horizontal)</SelectItem>
                              <SelectItem value="line">Line</SelectItem>
                              <SelectItem value="pie">Pie</SelectItem>
                              <SelectItem value="doughnut">Doughnut</SelectItem>
                              <SelectItem value="polarArea">Polar Area</SelectItem>
                              <SelectItem value="radar">Radar</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2 ml-auto">
                          <Button variant="outline" onClick={() => document.exitFullscreen?.()} aria-label="Sair da tela cheia">
                            <i className="fas fa-compress mr-2"></i>
                            Sair
                          </Button>
                          <Button variant="outline" onClick={exportChartPNG} aria-label="Exportar gráfico PNG">
                            <i className="fas fa-image mr-2"></i>
                            PNG
                          </Button>
                          <Button variant="outline" onClick={exportChartPDF} aria-label="Exportar gráfico PDF">
                            <i className="fas fa-file-pdf mr-2"></i>
                            PDF
                          </Button>
                        </div>
                      </div>
                    )}
                    <canvas ref={chartCanvasRef} />
                  </div>
                )}
                {chartFilterMode === 'institution' && chartInstitutionCouncilorRows.length > 0 && (
                  <div className="mt-4 w-full max-w-full overflow-x-hidden">
                    <table className="w-full text-sm table-fixed">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 w-3/4">Vereador</th>
                          <th className="text-right py-2 w-1/4">Total destinado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chartInstitutionCouncilorRows.map((row) => {
                          const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.total);
                          return (
                            <tr key={row.id} className="border-b hover:bg-muted/50">
                              <td className="py-2 pr-4 whitespace-normal break-words">
                                <div className="text-sm sm:text-base font-semibold">{row.name}</div>
                              </td>
                              <td className="py-2 text-right text-foreground">{formatted}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Valor Total indicado por instituição</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-4">
                  <div>
                    <label htmlFor="search" className="text-sm font-medium">Buscar Instituição</label>
                    <Input id="search" aria-label="Buscar por nome ou categoria da instituição" placeholder="Digite para filtrar" value={chartSearchQuery} onChange={(e) => setChartSearchQuery(e.target.value)} />
                  </div>

                  <div className="flex flex-wrap items-end gap-2 w-full min-w-0">
                    <Button variant="outline" className="w-full sm:w-auto" onClick={() => setSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc')} aria-label="Alternar ordenação">
                      <i className={`fas ${sortOrder === 'asc' ? 'fa-sort-amount-up' : 'fa-sort-amount-down'} mr-2`}></i>
                      Ordenar por valor ({sortOrder === 'asc' ? 'crescente' : 'decrescente'})
                    </Button>
                    <Button className="w-full sm:w-auto" onClick={exportCsv} aria-label="Exportar CSV">
                      <i className="fas fa-file-csv mr-2"></i>
                      Exportar CSV
                    </Button>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="advanced-filters">
                      <AccordionTrigger className="text-sm font-medium">Filtros avançados</AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="year" className="text-sm font-medium">Ano</label>
                            <Select value={filterYear} onValueChange={(v) => setFilterYear(v === 'all' ? '' : v)}>
                              <SelectTrigger id="year" aria-label="Filtrar por ano"><SelectValue placeholder="Todos" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {yearsAvailable.map((y) => (
                                  <SelectItem key={y} value={y}>{y}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label htmlFor="month" className="text-sm font-medium">Mês</label>
                            <Select value={filterMonth} onValueChange={(v) => setFilterMonth(v === 'all' ? '' : v)}>
                              <SelectTrigger id="month" aria-label="Filtrar por mês"><SelectValue placeholder="Todos" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((m) => (
                                  <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {loadingTotals ? (
                  <p className="text-muted-foreground">Carregando totais...</p>
                ) : computeInstitutionTotals.length === 0 ? (
                  <p className="text-muted-foreground">Nenhuma instituição com valores para os filtros selecionados.</p>
                ) : (
                  <div className="w-full max-w-full overflow-x-hidden">
                    <table className="w-full text-sm table-fixed">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 w-3/4">Instituição</th>
                          <th className="text-right py-2 w-1/4">Total Arrecadado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {computeInstitutionTotals.map((row) => {
                          const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.total);
                          const emphasize = row.total >= maxTotal * 0.7; // destaque para valores no top 30%
                          // row já possui categoriesDisplay consolidado
                          const description = row.categoriesDisplay || 'Sem categoria definida';
                          return (
                            <tr
                              key={row.id}
                              className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => openInstitutionDetails(row.name, row.institutionIds)}
                            >
                              <td className="py-2 pr-4 whitespace-normal break-words">
                                <div className="max-w-full break-words">
                                  <p className="text-[12px] leading-tight text-foreground opacity-80 mb-0.5">{description}</p>
                                  <div className="text-sm sm:text-base font-semibold text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline">
                                    {row.name}
                                  </div>
                                </div>
                              </td>
                              <td className={`py-2 text-right ${emphasize ? 'text-primary font-semibold' : 'text-foreground'}`}>
                                {formatted}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Modal de Detalhes da Instituição (Consolidado) */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex flex-col gap-1">
                <span>{selectedInstitutionGroup?.name}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  Detalhamento de emendas recebidas
                </span>
              </DialogTitle>
              <DialogDescription>
                Abaixo estão listadas todas as emendas destinadas a esta instituição (agrupadas por CNPJ).
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-8">
              {(() => {
                const grouped = getGroupedAmendmentsForModal();
                if (Object.keys(grouped).length === 0) {
                  return <p className="text-muted-foreground italic">Nenhuma emenda encontrada.</p>;
                }
                return Object.entries(grouped)
                  .sort((a, b) => {
                    const pa = styleForCategory(a[0]);
                    const pb = styleForCategory(b[0]);
                    const order: Record<StyleCategory, number> = { health: 1, education: 2, infrastructure: 3, diverse: 4 };
                    const diff = order[pa] - order[pb];
                    if (diff !== 0) return diff;
                    return a[0].localeCompare(b[0]);
                  })
                  .map(([cat, items]) => (
                    <AmendmentSection
                      key={cat}
                      title={getCategoryColors(cat).name || cat || 'Áreas Diversas'}
                      icon={`fas ${getCategoryColors(cat).icon || 'fa-tags'}`}
                      category={styleForCategory(cat)}
                      categoryKey={cat}
                      amendments={items}
                      headerColorClasses={{ bgClass: getCategoryColors(cat).bgClass, textClass: getCategoryColors(cat).textClass, borderClass: getCategoryColors(cat).borderClass }}
                      cardColorClasses={{ textClass: getCategoryColors(cat).textClass, borderClass: getCategoryColors(cat).borderClass, hoverClass: getCategoryColors(cat).hoverClass }}
                      onSelectAmendment={(id) => {
                        // setDetailsModalOpen(false); // Mantém aberto para stacking
                        setDetailAmendmentId(id);
                      }}
                    />
                  ));
              })()}
            </div>

            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setDetailsModalOpen(false)}>Fechar</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Detalhe da Emenda (Renderizado também na listagem geral) */}
        {(() => {
          if (!detailAmendmentId) return null;
          const a = amendments.find((x) => x.id === detailAmendmentId) || null;
          if (!a) return null;
          const inst = institutionMap[a.institutionId];
          const links: Array<{ label: string; url?: string }> = [
            { label: 'Site oficial', url: inst?.link },
            { label: 'Facebook', url: inst?.social?.facebook },
            { label: 'Instagram', url: inst?.social?.instagram },
            { label: 'X', url: inst?.social?.x },
            { label: 'TikTok', url: inst?.social?.tiktok },
            { label: 'YouTube', url: inst?.social?.youtube },
            { label: 'WhatsApp', url: inst?.social?.whatsapp },
          ];
          return (
            <InstitutionAmendmentModal
              open={!!detailAmendmentId}
              onOpenChange={(o) => {
                if (!o) {
                  setDetailAmendmentId(null);
                  if (returnToInstitutionList) {
                    setTimeout(() => {
                      setDetailsModalOpen(true);
                      setReturnToInstitutionList(false);
                    }, 100);
                  }
                }
              }}
              amendment={a}
              institution={inst || null}
              links={links}
            />
          );
        })()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-inter">
      {/* Header público com acesso ao Login */}
      <NavigationHeader />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => setSelectedCouncilor(null)}
            className="mb-4"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Voltar à Lista
          </Button>
        </div>

        <ParliamentarianHeader
          name={selectedCouncilor.name}
          position={'Vereador'}
          party={selectedCouncilor.party}
          reportTitle={`Relatório de Emendas dos Vereadores - ${new Date().getFullYear()}`}
          photoUrl={selectedCouncilor.photoUrl}
        />

        {loadingAmendments ? (
          <p className="text-muted-foreground">Carregando emendas...</p>
        ) : (
          <>
            {Object.entries(sections)
              .sort((a, b) => {
                // Ordena por prioridade de estilo (saúde, educação, infraestrutura, diversas), depois alfabético
                const pa = styleForCategory(a[0]);
                const pb = styleForCategory(b[0]);
                const order: Record<StyleCategory, number> = { health: 1, education: 2, infrastructure: 3, diverse: 4 };
                const diff = order[pa] - order[pb];
                if (diff !== 0) return diff;
                return a[0].localeCompare(b[0]);
              })
              .map(([cat, items]) => (
                <AmendmentSection
                  key={cat}
                  title={getCategoryColors(cat).name || cat || 'Áreas Diversas'}
                  icon={`fas ${getCategoryColors(cat).icon || 'fa-tags'}`}
                  category={styleForCategory(cat)}
                  categoryKey={cat}
                  amendments={items}
                  headerColorClasses={{ bgClass: getCategoryColors(cat).bgClass, textClass: getCategoryColors(cat).textClass, borderClass: getCategoryColors(cat).borderClass }}
                  cardColorClasses={{ textClass: getCategoryColors(cat).textClass, borderClass: getCategoryColors(cat).borderClass, hoverClass: getCategoryColors(cat).hoverClass }}
                  onSelectAmendment={(id) => setDetailAmendmentId(id)}
                />
              ))}
          </>
        )}

        <TotalsSummary />



        {/* Seção de Totais por Instituição no detalhe */}
        {/* <div className="mt-10">
          <Card>
            <CardHeader>
              <CardTitle>Valor Total indicado por instituiçãooo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-4">
                <div>
                  <label htmlFor="search-detail" className="text-sm font-medium">Buscar Instituição</label>
                  <Input id="search-detail" aria-label="Buscar por nome da instituição" placeholder="Digite o nome" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>

                <div className="flex flex-wrap items-end gap-2 w-full min-w-0">
                  <Button variant="outline" className="w-full sm:w-auto" onClick={() => setSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc')} aria-label="Alternar ordenação">
                    <i className={`fas ${sortOrder === 'asc' ? 'fa-sort-amount-up' : 'fa-sort-amount-down'} mr-2`}></i>
                    Ordenar por valor ({sortOrder === 'asc' ? 'crescente' : 'decrescente'})
                  </Button>
                  <Button className="w-full sm:w-auto" onClick={exportCsv} aria-label="Exportar CSV">
                    <i className="fas fa-file-csv mr-2"></i>
                    Exportar CSV
                  </Button>
                </div>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="advanced-filters-detail">
                    <AccordionTrigger className="text-sm font-medium">Filtros avançados</AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="year-detail" className="text-sm font-medium">Ano</label>
                          <Select value={filterYear} onValueChange={(v) => setFilterYear(v === 'all' ? '' : v)}>
                            <SelectTrigger id="year-detail" aria-label="Filtrar por ano"><SelectValue placeholder="Todos" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {yearsAvailable.map((y) => (
                                <SelectItem key={y} value={y}>{y}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label htmlFor="month-detail" className="text-sm font-medium">Mês</label>
                          <Select value={filterMonth} onValueChange={(v) => setFilterMonth(v === 'all' ? '' : v)}>
                            <SelectTrigger id="month-detail" aria-label="Filtrar por mês"><SelectValue placeholder="Todos" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((m) => (
                                <SelectItem key={m} value={m}>{m}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {loadingTotals ? (
                <p className="text-muted-foreground">Carregando totais...</p>
              ) : computeInstitutionTotals.length === 0 ? (
                <p className="text-muted-foreground">Nenhuma instituição com valores para os filtros selecionados.</p>
              ) : (
                <div className="w-full max-w-full overflow-x-hidden">
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 w-3/4">Instituição</th>
                        <th className="text-right py-2 w-1/4">Total Arrecadado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {computeInstitutionTotals.map((row) => {
                        const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.total);
                        const emphasize = row.total >= maxTotal * 0.7;
                        const inst = institutionMap[row.id];
                        const description = inst?.category || 'Instituição';
                        return (
                          <tr key={row.id} className="border-b hover:bg-muted/50">
                            <td className="py-2 pr-4 whitespace-normal break-words">
                              <div className="max-w-full break-words">
                                <p className="text-[12px] leading-tight text-foreground">{description}</p>
                                <div className="text-sm sm:text-base font-semibold">{row.name}</div>
                              </div>
                            </td>
                            <td className={`py-2 text-right ${emphasize ? 'text-primary font-semibold' : 'text-foreground'}`}>{formatted}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div> */}

        <footer className="text-center text-muted-foreground text-sm mt-12 pt-8 border-t border-border">
          <p className="mb-2">
            Dados extraídos do Sistema de Emendas Individuais - Portal da Transparência
          </p>
          <p>
            © <span id="currentYear">{new Date().getFullYear()}</span> DOMPR Câmara Municipal de Sinop - MT
          </p>
        </footer>

        {/* Modal de Detalhe da Emenda (Empilhado sobre a lista ou isolado) */}
        {(() => {
          if (!detailAmendmentId) return null;
          // Busca em todas as emendas para garantir que funcione mesmo sem vereador selecionado ou vindo do modal de totais
          const a = amendments.find((x) => x.id === detailAmendmentId) || null;
          if (!a) return null;
          const inst = institutionMap[a.institutionId];
          const copy = async (text: string) => { try { await navigator.clipboard.writeText(text); toast({ title: 'Copiado', description: text }); } catch { } };
          const links: Array<{ label: string; url?: string }> = [
            { label: 'Site oficial', url: inst?.link },
            { label: 'Facebook', url: inst?.social?.facebook },
            { label: 'Instagram', url: inst?.social?.instagram },
            { label: 'X', url: inst?.social?.x },
            { label: 'TikTok', url: inst?.social?.tiktok },
            { label: 'YouTube', url: inst?.social?.youtube },
            { label: 'WhatsApp', url: inst?.social?.whatsapp },
          ];
          return (
            <InstitutionAmendmentModal
              open={!!detailAmendmentId}
              onOpenChange={(o) => {
                if (!o) {
                  setDetailAmendmentId(null);
                  if (returnToInstitutionList) {
                    setTimeout(() => {
                      setDetailsModalOpen(true);
                      setReturnToInstitutionList(false);
                    }, 100);
                  }
                }
              }}
              amendment={a}
              institution={inst || null}
              links={links}
            />
          );
        })()}
      </div>
    </div>
  );
}
